import { FormikHandlers, FormikValues } from 'formik';

import {
  CheckboxProps,
  InputProps,
  SelectProps,
  SelectSize,
  TooltipProps,
  TypographyType,
} from '@lyearn/ui';

import { FieldVisibility } from '../types';
import { FieldValidateEntry, FieldValidationTypes } from './ValidationSchema';
import { FieldError, FieldValidation, ScrollBlockPosition } from './';

export enum FieldType {
  Text = 'TEXT',
  Email = 'EMAIL',
  Switch = 'SWITCH',
  Number = 'NUMBER',
  Custom = 'CUSTOM',
  SlowCustom = 'SLOW_CUSTOM',
  Password = 'PASSWORD',
  Picklist = 'PICKLIST',
  Checkbox = 'CHECKBOX',
  Multiline = 'MULTILINE',
  MultiPicklist = 'MULTI_PICKLIST',
  Rating = 'RATING',
}

export enum RatingType {
  Star = 'STAR',
  Number = 'NUMBER',
  Slider = 'SLIDER',
}

export interface PicklistValue {
  label: string;
  value: string;
}

export enum ErrorPosition {
  Top = 'TOP',
  Bottom = 'BOTTOM',
  Custom = 'CUSTOM',
}

// extend from FormCustomFieldRendererParams
export interface RendererParams<
  TValue extends any = any,
  TValues extends FormikValues = FormikValues,
> {
  formik: any;
  value: TValue;
  name: string;
  values: TValues;
  error: any; // type
  errors: any; // type
  touched: any; // boolean
  setStatus: (status?: any) => void;
  handleBlur: FormikHandlers['handleBlur'];
  handleChange: FormikHandlers['handleChange'];
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  onChange: (value: TValue) => void;
  setFieldError: (field: string, message: string | undefined) => void;
  setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
  setErrorElement: (element: HTMLElement | null) => void;
  setNudgeState: (fieldName: string, value: string) => void;
  field?: FormSlowCustomField<TValue, TValues>;
}

// add generic TValues
export interface FormCustomFieldRendererParams<TValue extends any = any> {
  value: TValue;
  name: string;
  error: any; // type
  touched: boolean;
  onChange: (value: TValue) => void;
  // type
  setFieldError: (field: string, message: string | undefined) => void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
  setErrorElement: (element: HTMLElement | null) => void;
  setNudgeState: (fieldName: string, value: string) => void;
}

interface ClassNameProps {
  className?: string;
}

export type TypographyOverrides = Pick<TypographyType, 'color' | 'variant' | 'as' | 'className'>;

export interface SectionOverrides {
  Container?: ClassNameProps;
  Header?: ClassNameProps;
  Name?: TypographyOverrides;
  Description?: TypographyOverrides;
  spacing?: string;
}

export interface FieldOverrides {
  Label?: TypographyOverrides;
  Description?: TypographyOverrides;
  Error?: TypographyOverrides;
  FieldContainer?: ClassNameProps;
  Section?: SectionOverrides;
  spacing?: {
    row?: string;
    column?: string;
  };
  MultiStepTabs?: ClassNameProps;
}

interface CommonFormField<TValue extends any = any, TValues extends FormikValues = FormikValues> {
  name: string;
  required?: boolean;
  disabled?: boolean;
  label?: React.ReactNode;
  description?: React.ReactNode;
  tooltipHelperText?: string;
  tooltipHelperTextPlacement?: TooltipProps['placement'];
  visibility?: FieldVisibility[];
  validation?: FieldValidation<TValue, TValues>;
  errorPosition?: ErrorPosition;
  scrollBlockPosition?: ScrollBlockPosition;
}

export interface FormCheckboxField<TValues extends FormikValues = FormikValues>
  extends CommonFormField<boolean, TValues> {
  type: FieldType.Checkbox;
  props?: Pick<CheckboxProps, 'defaultChecked'>;
}

export interface FormSwitchField<TValues extends FormikValues = FormikValues>
  extends CommonFormField<boolean, TValues> {
  type: FieldType.Switch;
}

export interface FormSlowCustomField<
  TValue extends any = any,
  TValues extends FormikValues = FormikValues,
> extends CommonFormField<TValue, TValues> {
  type: FieldType.SlowCustom;
  placeholder?: InputProps['placeholder'];
  rendererProps?: any; // type
  Component: React.FunctionComponent<RendererParams<TValue, TValues>>;
}

export interface FormCustomField<
  TValue extends any = any,
  TValues extends FormikValues = FormikValues,
> extends CommonFormField<TValue, TValues> {
  type: FieldType.Custom;
  Component: React.FunctionComponent<FormCustomFieldRendererParams<TValue>>;
}

export interface FormEmailField<TValues extends FormikValues = FormikValues>
  extends CommonFormField<string, TValues> {
  type: FieldType.Email | FieldType.Number | FieldType.Password; // ???: email can be number or password?
  placeholder?: InputProps['placeholder'];
  size?: InputProps['size'];
}

export interface FormTextField<TValues extends FormikValues = FormikValues>
  extends CommonFormField<string, TValues> {
  type: FieldType.Text | FieldType.Multiline;
  placeholder?: InputProps['placeholder'];
  size?: InputProps['size'];
  props?: Pick<
    InputProps,
    | 'autoFocus'
    | 'characterLimit'
    | 'className'
    | 'rows'
    | 'variant'
    | 'maxRows'
    | 'inputProps'
    | 'endAdornment'
    | 'startAdornment'
    | 'inputRef'
    | 'size'
    | 'disabled'
  >;
}

// TODO: Need to fix this, +1
type PicklistOption = any;

export interface FormPicklistField<
  T extends PicklistValue = PicklistValue,
  TValues extends FormikValues = FormikValues,
> extends CommonFormField<string | string[], TValues> {
  type: FieldType.Picklist | FieldType.MultiPicklist;
  placeholder?: string;
  size?: SelectSize;
  picklistValues: T[];
  props?: Pick<
    SelectProps<PicklistOption, false, false>,
    | 'optionLabelRenderer'
    | 'renderValue'
    | 'className'
    | 'disableCloseOnSelect'
    | 'blurOnSelect'
    | 'classes'
    | 'tagLabelRenderer'
    | 'renderTags'
  >;
}

export type SliderMarks = { value: number; label: string }[];

export interface FormRatingField<TValues extends FormikValues = FormikValues>
  extends CommonFormField<number, TValues> {
  type: FieldType.Rating;
  props: {
    max?: number;
    min?: number;
    maxLabel?: string;
    minLabel?: string;
    ratingType: RatingType;
    // for slider rating only
    step?: number | null;
    marks?: SliderMarks;
  };
}

export type Field<TValue extends any = any, TValues extends FormikValues = FormikValues> =
  | FormCheckboxField<TValues>
  | FormSwitchField<TValues>
  | FormCustomField<TValue, TValues>
  | FormSlowCustomField<TValue, TValues>
  | FormEmailField<TValues>
  | FormTextField<TValues>
  | FormPicklistField<PicklistValue, TValues>
  | FormRatingField<TValues>;

export type FieldValidationParams<
  TValue extends any = any,
  TValues extends FormikValues = FormikValues,
> = {
  field: Field<TValue, TValues>;
  value: TValue;
  validation: FieldValidateEntry<TValue, TValues>;
};

export type FieldValidationFunction<
  TValue extends any = any,
  TValues extends FormikValues = FormikValues,
> = Record<FieldValidationTypes, (params: FieldValidationParams<TValue, TValues>) => FieldError>;
